import startCase from 'lodash-es/startCase'
import React, { useEffect, useState } from 'react'

import { TabPills } from '@components/common'
import { ScrollContent } from '@components/common/ScrollContent'
import {
  Button,
  ButtonGroup,
  Icon,
  Loader,
  ModalBox,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { MicrophoneIcon, SwitchIcon, VideoIcon } from '@fourthwall/icons'

import type { ThankYou } from '../../ThankYous.types'
import * as S from './SendThankYouModal.styled'
import type { ThankYouType } from './SendThankYouModal.types'
import { ActionButton } from './components/ActionButton/ActionButton'
import { AudioPlayer } from './components/AudioPlayer/AudioPlayer'
import { FooterTabs } from './components/FooterTabs/FooterTabs'
import { Layout } from './components/Layout/Layout'
import { LayoutMobile } from './components/LayoutMobile/LayoutMobile'
import { MediaSourceButton } from './components/MediaSourceButton/MediaSourceButton'
import { MissingPermissions } from './components/MissingPermissions/MissingPermissions'
import { OrderDetails } from './components/OrderDetails/OrderDetails'
import { Sidebar } from './components/Sidebar/Sidebar'
import { SkipButton } from './components/SkipButton/SkipButton'
import { ThankYousNotSupported } from './components/ThankYousNotSupported/ThankYousNotSupported'
import { TopBar } from './components/TopBar/TopBar'
import { UploadActionButton } from './components/UploadActionButton/UploadActionButton'
import { UploadLoader } from './components/UploadLoader/UploadLoader'
import { UploadMedia } from './components/UploadMedia/UploadMedia'
import { UploadSuccess } from './components/UploadSuccess/UploadSuccess'
import { useSendThankYou } from './hooks/useSendThankYou'
import { millisecondsToTime } from './utils/millisecondsToTime'

export type SendThankYouModalProps = {
  thankYous: ThankYou[]
  onSuccess?: () => void
}

export const SendThankYouModal = ({ thankYous, onSuccess }: SendThankYouModalProps) => {
  const videoStreamRef = React.useRef<HTMLVideoElement>(null)
  const [showOrderDetails, setShowOrderDetails] = useState(true)
  const { laptop } = useMediaQueryContext()
  const {
    thankYou,
    thankYouType,
    thankYouTypes,
    media,
    areThankYousSupported,
    isSkipping,
    isUploading,
    isSuccess,
    setThankYouType,
    onThankYouSkip,
    onThankYouSend,
    onContinue,
    onClose,
  } = useSendThankYou({
    thankYous,
    onSuccess,
  })

  const { close } = useModalContext()

  useEffect(() => {
    if (videoStreamRef.current && media.stream && media.status === 'IDLE') {
      videoStreamRef.current.srcObject = media.stream
    }
  }, [media.status, media.stream])

  const handleClose = () => {
    onClose?.()
    close()
  }

  const renderMainContentHeader = () => {
    if (isUploading || isSuccess) return null

    if (
      media.status === 'IDLE' ||
      media.status === 'MISSING_PERMISSIONS' ||
      media.status === 'LOADING'
    ) {
      return (
        <S.HeaderTabs>
          <TabPills
            appearance="secondary"
            value={thankYouType}
            options={thankYouTypes.map((tab) => ({
              label: startCase(tab.toLocaleLowerCase()),
              value: tab,
            }))}
            onChange={(value) => setThankYouType(value as ThankYouType)}
          />
        </S.HeaderTabs>
      )
    }

    return null
  }

  const renderMainContentFooter = () => {
    if (
      media.status === 'MISSING_PERMISSIONS' ||
      media.status === 'LOADING' ||
      isUploading ||
      isSuccess ||
      thankYou.state === 'SENT'
    )
      return null

    if (media.status === 'IDLE' && (thankYouType === 'VIDEO' || thankYouType === 'AUDIO')) {
      return (
        <Button
          key="start-recording"
          leftIcon={<S.StartRecordingButtonIcon />}
          appearance="destructive"
          label="Start recording"
          onClick={media.onRecordStart}
        />
      )
    }

    if (media.status === 'IN_PROGRESS') {
      return (
        <Button
          key="stop-recording"
          leftIcon={<S.StopRecordButtonIcon />}
          appearance="destructive-inverted"
          label="Stop recording"
          onClick={media.onRecordStop}
        />
      )
    }

    if (media.status === 'PREVIEW') {
      return (
        <ButtonGroup space="xlarge">
          <Button appearance="secondary" label="Cancel" onClick={media.onRecordDelete} />
          <Button
            appearance="primary"
            label="Send thank you"
            onClick={onThankYouSend}
            disabled={!!media.preview?.error}
          />
        </ButtonGroup>
      )
    }

    return null
  }

  const renderPreviewError = () => {
    if (media.preview?.error === 'FILE_TOO_LARGE' && thankYouType === 'UPLOAD') {
      return `Uploaded file exceeds the maximum size of 50MB.`
    }

    if (media.preview?.error === 'FILE_TOO_LARGE' && thankYouType === 'AUDIO') {
      return `Recorded audio exceeds the maximum size of 50MB.`
    }

    if (media.preview?.error === 'FILE_TOO_LARGE' && thankYouType === 'VIDEO') {
      return `Recorded video exceeds the maximum size of 50MB.`
    }

    return null
  }

  const renderMainContentFooterTop = () => {
    if (media.status === 'IN_PROGRESS' && thankYouType === 'VIDEO') {
      return <S.FooterTimer>{millisecondsToTime(media.duration)}</S.FooterTimer>
    }

    if (media.status === 'PREVIEW' && !!media.preview?.error) {
      return <S.FooterError>{renderPreviewError()}</S.FooterError>
    }

    return null
  }

  const renderMainContentFooterRight = () => {
    if (isUploading || isSuccess) return null

    if (media.status === 'IDLE' && onThankYouSkip) {
      return <SkipButton isLoading={isSkipping} onClick={onThankYouSkip} />
    }

    return null
  }

  const renderMainContentFooterLeft = () => {
    if (isUploading || isSuccess || !media.sources) return null

    if (media.status === 'IDLE' && (thankYouType === 'VIDEO' || thankYouType === 'AUDIO')) {
      return (
        <S.MediaSourceButtons>
          {'onVideoSourceChange' in media.sources && media.sources.video.length > 0 && (
            <MediaSourceButton
              options={media.sources.video}
              icon={<Icon component={VideoIcon} height={20} />}
              onChange={media.sources.onVideoSourceChange}
            />
          )}
          {'onAudioSourceChange' in media.sources && media.sources.audio.length > 0 && (
            <MediaSourceButton
              options={media.sources.audio}
              icon={<Icon component={MicrophoneIcon} height={20} />}
              onChange={media.sources.onAudioSourceChange}
            />
          )}
        </S.MediaSourceButtons>
      )
    }

    return null
  }

  const renderContent = () => {
    if (isUploading) return <UploadLoader />

    if (isSuccess)
      return (
        <UploadSuccess
          supporterName={thankYou.supporter.fullName}
          onContinue={onContinue}
          onClose={handleClose}
        />
      )

    if (media.status === 'MISSING_PERMISSIONS') {
      return <MissingPermissions onTryAgain={media.onReinitialize} />
    }

    if (
      (media.status === 'LOADING' || media.status === 'IDLE' || media.status === 'IN_PROGRESS') &&
      thankYouType === 'VIDEO'
    ) {
      return (
        <>
          {media.status === 'LOADING' && (
            <S.LoaderContainer>
              <Loader size="large" appearance="primary" />
            </S.LoaderContainer>
          )}
          <S.VideoElement ref={videoStreamRef} key="record" autoPlay muted playsInline />
        </>
      )
    }

    if (media.status === 'LOADING' && thankYouType === 'AUDIO') {
      return (
        <S.LoaderContainer>
          <Loader size="large" appearance="primary" />
        </S.LoaderContainer>
      )
    }

    if ((media.status === 'IDLE' || media.status === 'IN_PROGRESS') && thankYouType === 'AUDIO') {
      return (
        <S.Timer $visible={laptop || !showOrderDetails}>
          {millisecondsToTime(media.duration)}
        </S.Timer>
      )
    }

    if ((media.status === 'IDLE' || media.status === 'IN_PROGRESS') && thankYouType === 'UPLOAD') {
      return (
        <S.UploadMediaWrapper $hidden={!laptop && showOrderDetails}>
          <UploadMedia
            onFileDrop={media.onFileDrop}
            onFileDropRejected={media.onFileDropRejected}
          />
        </S.UploadMediaWrapper>
      )
    }

    if (media.status === 'PREVIEW' && media.preview?.type === 'VIDEO') {
      return (
        <S.VideoElement
          src={media.preview.url}
          key="preview"
          controls={laptop}
          autoPlay={!laptop}
          loop={!laptop}
          playsInline
        />
      )
    }

    if (media.status === 'PREVIEW' && media.preview?.type === 'IMAGE') {
      return <S.ImageElement src={media.preview.url} key="preview" />
    }

    if (media.status === 'PREVIEW' && media.preview?.type === 'AUDIO') {
      return <AudioPlayer src={media.preview.url} />
    }

    return null
  }

  const renderContentFooter = () => {
    if (
      media.status === 'MISSING_PERMISSIONS' ||
      media.status === 'LOADING' ||
      isUploading ||
      isSuccess ||
      thankYou.state === 'SENT'
    )
      return null

    if (media.status === 'IDLE' && (thankYouType === 'VIDEO' || thankYouType === 'AUDIO')) {
      return (
        <S.ContentFooterContainer>
          <ActionButton
            actionType={thankYouType === 'VIDEO' ? 'video' : 'audio'}
            onClick={media.onRecordStart}
          />
          {thankYouType === 'VIDEO' && (
            <S.SwitchCameraButton
              component={SwitchIcon}
              height={16}
              onClick={media.onSwitchFacingMode}
            />
          )}
          {thankYouType === 'AUDIO' && (
            <S.TimerWrapper>
              <S.Timer $small $visible={showOrderDetails}>
                {millisecondsToTime(media.duration)}
              </S.Timer>
            </S.TimerWrapper>
          )}
        </S.ContentFooterContainer>
      )
    }

    if (media.status === 'IDLE' && thankYouType === 'UPLOAD') {
      return (
        <S.ContentFooterContainer>
          <UploadActionButton
            onFileDrop={media.onFileDrop}
            onFileDropRejected={media.onFileDropRejected}
          />
        </S.ContentFooterContainer>
      )
    }

    if (media.status === 'IN_PROGRESS') {
      return (
        <S.ContentFooterContainer>
          <ActionButton actionType="stop" onClick={media.onRecordStop} />
          {thankYouType === 'AUDIO' && (
            <S.TimerWrapper>
              <S.Timer $small $visible={showOrderDetails}>
                {millisecondsToTime(media.duration)}
              </S.Timer>
            </S.TimerWrapper>
          )}
        </S.ContentFooterContainer>
      )
    }

    if (media.status === 'PREVIEW') {
      return (
        <S.ContentFooterContainer>
          <ActionButton actionType="send" onClick={onThankYouSend} />
        </S.ContentFooterContainer>
      )
    }
  }

  if (!areThankYousSupported) {
    return <ThankYousNotSupported />
  }

  if (!laptop) {
    return (
      <ModalBox withoutPadding withoutCloseButton size="fullScreen">
        <LayoutMobile
          content={renderContent()}
          contentHeader={
            <S.ContentHeaderContainer>
              <TopBar
                thankYou={thankYou}
                media={media}
                isSuccess={isSuccess}
                isUploading={isUploading}
                showOrderDetails={showOrderDetails}
                onToggleShowOrderDetails={() => setShowOrderDetails((state) => !state)}
                onClose={handleClose}
                onSkip={onThankYouSkip}
              />

              {['IDLE', 'IN_PROGRESS'].includes(media.status) && (
                <S.ContentHeaderDetails $hidden={!showOrderDetails}>
                  <ScrollContent maxHeight={window.innerHeight / 2.5} hideScrollbar>
                    <OrderDetails thankYou={thankYou} />
                  </ScrollContent>
                </S.ContentHeaderDetails>
              )}
            </S.ContentHeaderContainer>
          }
          contentFooter={renderContentFooter()}
          hideFooter={
            media.status === 'MISSING_PERMISSIONS' ||
            media.status === 'LOADING' ||
            isUploading ||
            isSuccess ||
            thankYou.state === 'SENT'
          }
          footer={
            media.status === 'IDLE' ? (
              <FooterTabs
                tabs={thankYouTypes}
                activeTab={thankYouType}
                onTabChange={setThankYouType}
              />
            ) : null
          }
        />
      </ModalBox>
    )
  }

  return (
    // NOTE: width is calculated to maintain 16/9 video aspect ratio and sidebar content
    <ModalBox size="large" width="calc(88vh + 400px)" withoutPadding onClose={handleClose}>
      <Layout
        appearance={isSuccess ? 'LIGHT' : 'DARK'}
        content={renderContent()}
        contentHeader={renderMainContentHeader()}
        contentFooter={renderMainContentFooter()}
        contentFooterTop={renderMainContentFooterTop()}
        contentFooterRight={renderMainContentFooterRight()}
        contentFooterLeft={renderMainContentFooterLeft()}
        sidebar={!isSuccess ? <Sidebar thankYou={thankYou} /> : null}
      />
    </ModalBox>
  )
}
